import './SingleChannelDeliveryChart.scss';
import { Bar, CartesianGrid, Cell, ComposedChart, Line, XAxis, YAxis } from 'recharts';
import { AxisDomain } from 'recharts/types/util/types';
import {
  getCurrencySymbol,
  getMetricFormatter
} from '@blisspointmedia/bpm-types/dist/metricFormatUtils';
import { toPrettyPercent } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { MdClose, MdArrowBackIos, MdArrowForwardIos } from 'react-icons/md';
import { Metric } from '@blisspointmedia/bpm-types/dist/CrossChannel';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import Papa from 'papaparse';
import {
  Dispatch,
  JSX,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { DimensionColumn } from '@blisspointmedia/bpm-types/dist/MetricsTable';
import {
  CompanyFiscalCal,
  DateGroupingKey,
  getGroupingValue
} from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { FilterPaneState } from '@blisspointmedia/bpm-types/dist/FilterPane';
import cn from 'classnames';
import { abbreviateNumber } from '@src/utils/data';
import {
  awaitJSON,
  fetchWithCometFallback,
  LinearLambdaFetch,
  pollS3,
  StreamingV2LambdaFetch,
  fetchPresigned
} from '@src/utils/fetch-utils';
import { DATE_GROUPING_OPTIONS, DateRange, formatDateLabel } from '@src/utils/date-utils';
import { downloadPNG, exportToExcel } from '@src/utils/download-utils';
import { getChannelSeriesColor, shouldUsePrimaryPalette } from '@src/utils/colors';
import { SingleChannelMediaTypes } from '@src/utils/performance-utils';
import { useSetError } from '@src/redux/modals';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { withRatioAggregators } from '@src/utils/metrics/ratioAggregation';
import {
  InvalidGranularityDetails,
  makeInvalidDimMsg
} from '@src/utils/columnMetricSystem/configUtils';
import { ChartContainer } from '@Components/ChartContainer';
import { PercentChange } from '@Components/PercentChange';
import { Spinner } from '@Components/Spinner';
import { Img } from '@Components/Img';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { GraphTableToggleButton } from '@Components/GraphTableToggleButton';
import { BPMTable, TypedHeader, SuperTopItemType } from '@Components/StickyTable';
import { Dropdown, DropdownToggleType } from '@Components/Dropdown';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Placement, Size, StretchMode } from '@Components/designConstants';
import {
  CARTESIAN_GRID_STROKE,
  CARTESIAN_GRID_STROKE_WIDTH,
  TICK_STYLE,
  X_AXIS_PADDING
} from '@Components/Charts/ChartConstants';
import { CellData, DimensionMap } from '@Components/MetricsTable/metrics-table-utils';
import { METRIC_TO_PRETTY_NAME } from '@Pages/CrossChannel/cross-channel-constants';
import {
  getCompanyChannelColumnMetaData,
  sharedColumnMetaDataMap
} from '../Channels/shared-performance-utils';
import { DailyDeliveryData } from './delivery-fetch';
import { buildDeliveryCsvParams, supportsDeliveryCsv } from './delivery-utils';

const DOWNLOAD_FULL_DETAIL = 'Full detail (XLSX)';
const DOWNLOAD_CHART_VIEW = 'Chart view (XLSX)';
const DOWNLOAD_PNG = 'PNG';
const DOWNLOAD_POST_LOGS = 'Post Logs';
const DELIVERY_NUMERIC_COLUMNS = [
  'Count',
  'Cost',
  'MediaCost',
  'MediaCostWithCommission',
  'NonMediaCost'
];

export interface DeliveryMetricOption {
  fetchGetter?: (fetch: Record<string, number>) => number;
  isCustom?: boolean;
  isSpend?: boolean;
  label: string;
  value: string;
}

export const DEFAULT_DELIVERY_METRICS_OPTIONS: DeliveryMetricOption[] = [
  { label: 'Spend', isSpend: true, value: 'cost' },
  { label: 'Impressions', value: 'count' }
];
// No palette equivalent — a one-off desaturated grey used only to grey out de-emphasized chart
// bars/legend swatches, not a UI or brand color.
const FEE_BREAKOUT_METRICS = ['mediaCost', 'nonMediaCost'];
const GREYED_OUT_GREY = '#C7C7C7';
// CPM-family efficiency metrics render against a currency axis; rate metrics
// (e.g. ctrEmail) render as a percentage.
const CURRENCY_EFFICIENCY_METRICS = ['cpm', 'eCpm', 'youtubeCpm'];
// End at the data max instead of recharts 3's nicing up to a labeled round top tick.
const Y_AXIS_DOMAIN: AxisDomain = [(dataMin: number) => Math.min(0, dataMin), 'dataMax'];
// Headroom so the 3px CPM line and tallest bar aren't clipped by the plot's top edge.
const Y_AXIS_PADDING = { top: 4 };
const TICK_STEP_MULTIPLIERS = [1, 2, 2.5, 5, 10];
const TARGET_TICK_COUNT = 4;
// Ticks at nice steps (1/2/2.5/5 x 10^n) from 0 up to, but never at or above, the axis max.
const getNiceTicks = (max: number): number[] | undefined => {
  if (!(max > 0)) return undefined;
  const rawStep = max / TARGET_TICK_COUNT;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const step = magnitude * (TICK_STEP_MULTIPLIERS.find(m => m * magnitude >= rawStep) ?? 10);
  const ticks: number[] = [];
  for (let i = 0; i * step < max; i++) {
    // Rounded to dodge floating-point noise (e.g. 0.30000000000000004).
    ticks.push(Number((i * step).toPrecision(12)));
  }
  return ticks;
};
const LEFT_Y_AXIS_ID = 0;
const RIGHT_Y_AXIS_ID = 1;
const PRIMARY_STACK_ID = 0;
const COMPARISON_STACK_ID = 1;

type DeliveryMetricValues = Record<string, number>;
type DeliveryDimensionMap = Record<string, DeliveryMetricValues>;
type DeliveryDateMap = Record<string, DeliveryDimensionMap>;

interface JoinedDeliveryRow {
  date: string;
  datePrimary?: string;
  otherDate?: string;
  data: DeliveryDimensionMap;
  otherData: DeliveryDimensionMap;
}

interface OverviewTotal {
  primary: number;
  comparison: number;
}

interface DeliveryTableRow {
  data: DeliveryMetricValues;
  date: string;
  dimensionValue: string;
  otherData: DeliveryMetricValues;
  otherDate: string;
}

const isEmptyRecord = (obj: Record<string, unknown> | undefined | null): boolean =>
  obj == null || Object.keys(obj).length === 0;

/**
 * Depending on the date grouping selected (Day, Week, Month, etc.), sum up the metrics for each date.
 */
const deliveryTotalsByDateIncrement = (
  data: DailyDeliveryData,
  dateGrouping: DateGroupingKey,
  deliveryMetricOptions: DeliveryMetricOption[],
  fiscalCal: CompanyFiscalCal
): DeliveryDateMap => {
  const totalsByDate: DeliveryDateMap = {};
  // DailyDeliveryDatum declares concrete fields (cost/ratedCost/count/spots); the metric-value
  // keys read below are dynamic (driven by DeliveryMetricOption.value), so treat each dimension's
  // cell as a generic numeric map rather than the concrete datum shape.
  const smallestDataIncrement = ((!isEmptyRecord(data.daily) || isEmptyRecord(data.weekly)
    ? data.daily
    : data.weekly) ?? {}) as unknown as DeliveryDateMap;
  for (const date of Object.keys(smallestDataIncrement)) {
    const dateToUse = getGroupingValue(date, dateGrouping, fiscalCal);
    totalsByDate[dateToUse] = totalsByDate[dateToUse] || {};
    for (const dim of Object.keys(smallestDataIncrement[date])) {
      totalsByDate[dateToUse][dim] = totalsByDate[dateToUse][dim] || {};
      for (const metric of deliveryMetricOptions) {
        totalsByDate[dateToUse][dim][metric.value] =
          (totalsByDate[dateToUse][dim][metric.value] || 0) +
          (smallestDataIncrement[date][dim][metric.value] || 0);
      }
      for (const feeMetric of FEE_BREAKOUT_METRICS) {
        totalsByDate[dateToUse][dim][feeMetric] =
          (totalsByDate[dateToUse][dim][feeMetric] || 0) +
          (smallestDataIncrement[date][dim][feeMetric] || 0);
      }
    }
  }
  for (const date of Object.keys(smallestDataIncrement)) {
    const dateToUse = getGroupingValue(date, dateGrouping, fiscalCal);
    for (const dim of Object.keys(smallestDataIncrement[date])) {
      const fetch = totalsByDate[dateToUse][dim];
      for (const metric of deliveryMetricOptions) {
        if (metric.isCustom && metric.fetchGetter) {
          totalsByDate[dateToUse][dim] = {
            ...totalsByDate[dateToUse][dim],
            [metric.value]: metric.fetchGetter(fetch)
          };
        }
      }
    }
  }
  return totalsByDate;
};

const joinDeliveryData = (
  data: DeliveryDateMap,
  otherData: DeliveryDateMap,
  keys: string[],
  includeOtherDateRange: boolean,
  compareDateRange: boolean,
  emptyCell: DeliveryMetricValues
): JoinedDeliveryRow[] => {
  const res: JoinedDeliveryRow[] = [];
  const dates = Object.keys(data).sort((a, b) => a.localeCompare(b));
  const otherDates = Object.keys(otherData).sort((a, b) => a.localeCompare(b));
  const allDates = includeOtherDateRange
    ? [...new Set([...dates, ...otherDates])].sort((a, b) => a.localeCompare(b))
    : dates;

  if (compareDateRange) {
    const longerRange = dates.length > otherDates.length ? dates : otherDates;
    for (let dateIndex = 0; dateIndex < longerRange.length; dateIndex++) {
      const dateToUse = longerRange[dateIndex];
      const date = dates[dateIndex];
      const otherDate = otherDates[dateIndex];
      const joinedData: JoinedDeliveryRow = {
        date: dateToUse,
        datePrimary: date,
        otherDate,
        data: {},
        otherData: {}
      };
      for (const key of keys) {
        joinedData.data[key] = (date && data[date]?.[key]) || emptyCell;
        joinedData.otherData[key] = otherData[otherDate]?.[key] || emptyCell;
      }
      res.push(joinedData);
    }
  } else {
    for (const date of allDates) {
      const joinedData: JoinedDeliveryRow = { date, data: {}, otherData: {} };
      for (const key of keys) {
        joinedData.data[key] = data[date]?.[key] || emptyCell;
        joinedData.otherData[key] = otherData[date]?.[key] || emptyCell;
      }
      res.push(joinedData);
    }
  }
  return res;
};

interface SingleChannelDeliveryChartProps {
  company: string;
  data: DailyDeliveryData;
  dates: DateRange;
  defaultDateGroupingKey?: DateGroupingKey;
  defaultMetric?: string;
  deliveryMetricOptions?: DeliveryMetricOption[];
  dimension: string;
  // prettyName -> snake_case name for each delivery dimension
  deliveryDimensionMap: Record<string, string>;
  getDimensionCell: (
    dimensionData: DimensionMap,
    dimensionHeader: DimensionColumn,
    ...args: unknown[]
  ) => CellData;
  otherData: DailyDeliveryData;
  otherDates?: DateRange;
  prefix: SingleChannelMediaTypes;
  setDimension: Dispatch<SetStateAction<string>>;
  efficiencyMetric?: string;
  incompatibleDeliveryDimsFromFilter?: InvalidGranularityDetails;
  filterID?: number;
  filterState?: FilterPaneState;
}

export const SingleChannelDeliveryChart = ({
  company,
  data,
  dates,
  defaultDateGroupingKey = 'Day',
  defaultMetric,
  deliveryMetricOptions = DEFAULT_DELIVERY_METRICS_OPTIONS,
  dimension,
  deliveryDimensionMap,
  getDimensionCell,
  otherData,
  otherDates,
  prefix,
  setDimension,
  efficiencyMetric = 'cpm',
  incompatibleDeliveryDimsFromFilter = {},
  filterID,
  filterState
}: SingleChannelDeliveryChartProps): JSX.Element => {
  const [absolute, setAbsolute] = useState(true);
  const [graphWidth, setGraphWidth] = useState<number>(0);
  // graphWidth mirrors #dc-graph's width onto the dc-above-graph sibling. Calling setGraphWidth
  // directly inside AutoSizer's renderProp (as bpm-frontend did) runs during AutoSizer's own
  // render, which triggers React's "update while rendering a different component" warning.
  // Observe the same container directly instead, so the state update happens in this
  // component's own effect.
  const graphContainerRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = graphContainerRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(([entry]) => setGraphWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const [groupingKey, setGroupingKey] = useState(defaultDateGroupingKey);
  const [hoveredDateIndex, setHoveredDateIndex] = useState<number | undefined>();
  const [hoveredLegendItemIndex, setHoveredLegendItemIndex] = useState<number | undefined>();
  const [includeOtherDateRange, setIncludeOtherDateRange] = useState<boolean>(false);
  const [metric, setMetric] = useState<DeliveryMetricOption>(() => {
    if (!defaultMetric) return deliveryMetricOptions[0];
    const matchedOption = deliveryMetricOptions.find(option => option.label === defaultMetric);
    return matchedOption
      ? { label: defaultMetric, value: matchedOption.value }
      : deliveryMetricOptions[0];
  });
  const [selectedDateIndex, setSelectedDateIndex] = useState<number | undefined>();
  const [showAddOtherDateRange, setShowAddOtherDateRange] = useState(false);
  const [selectedLegendItemIndexes, setSelectedLegendItemIndexes] = useState<
    number[] | undefined
  >();
  const [graphView, setGraphView] = useState(true);
  const [showPercentChange, setShowPercentChange] = useState(false);
  const [downloadingFromServer, setDownloadingFromServer] = useState(false);
  const setError = useSetError();
  const { fiscal_cal: fiscalCal, currency_code: currencyCode } = useCompanyInfo();
  const metricFormatter = useMemo(() => getMetricFormatter(currencyCode), [currencyCode]);
  const [spendFormatter, numberFormatter] = useMemo(
    () => [metricFormatter(Metric.SPEND), metricFormatter('number')],
    [metricFormatter]
  );
  const columnMetaData = useMemo(
    () =>
      withRatioAggregators(getCompanyChannelColumnMetaData(sharedColumnMetaDataMap, currencyCode)),
    [currencyCode]
  );
  const currSymbol = useMemo(() => getCurrencySymbol(currencyCode), [currencyCode]);

  const dateGroupingOptions = useMemo((): {
    label: string;
    value: DateGroupingKey;
    className?: string;
  }[] => {
    const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
    const diffDays = Math.round(
      Math.abs((new Date(dates.start).getTime() - new Date(dates.end).getTime()) / oneDay)
    );
    const diffOtherDays = !otherDates
      ? 0
      : Math.round(
          Math.abs(
            (new Date(otherDates.start).getTime() - new Date(otherDates.end).getTime()) / oneDay
          )
        );
    const maxDateRange = Math.max(diffDays, diffOtherDays);
    const dateGroupingsAllowed = [365, 90, 30, 7].filter(
      threshold => maxDateRange >= threshold
    ).length;
    const filteredDateGroupOptions: {
      label: string;
      value: DateGroupingKey;
      className?: string;
    }[] = [];
    for (let i = 0; i < DATE_GROUPING_OPTIONS.length; i++) {
      filteredDateGroupOptions.push({
        label: DATE_GROUPING_OPTIONS[i].label,
        value: DATE_GROUPING_OPTIONS[i].value,
        className: dateGroupingsAllowed < i ? 'disabled' : ''
      });
    }
    if (
      data?.daily &&
      otherData?.daily &&
      (!isEmptyRecord(data.daily) || (isEmptyRecord(data.daily) && isEmptyRecord(data.weekly))) &&
      (!isEmptyRecord(otherData.daily) ||
        (isEmptyRecord(otherData.daily) && isEmptyRecord(otherData.weekly)))
    ) {
      return filteredDateGroupOptions;
    }
    if (data?.weekly && otherData?.weekly) {
      return [
        { label: 'Weekly', value: 'Week' },
        ...filteredDateGroupOptions.filter(
          option => option.value !== 'Day' && option.value !== 'Week'
        )
      ];
    }
    return [];
  }, [data, dates, otherData, otherDates]);

  const sortedDimensionKeys = useMemo(
    () => [
      ...new Set([
        ...Object.keys(data.keys || {}).sort(),
        ...(otherData?.keys && includeOtherDateRange ? Object.keys(otherData.keys) : []).sort()
      ])
    ],
    [data, otherData, includeOtherDateRange]
  );

  const activeKeyCount = sortedDimensionKeys.length;

  const usePrimaryPalette = shouldUsePrimaryPalette(activeKeyCount);

  const dataGroupedByDate = useMemo(
    () =>
      data == null
        ? undefined
        : deliveryTotalsByDateIncrement(data, groupingKey, deliveryMetricOptions, fiscalCal),
    [data, deliveryMetricOptions, groupingKey, fiscalCal]
  );
  const otherDateDataGroupedByDate = useMemo(
    () =>
      otherData == null
        ? undefined
        : deliveryTotalsByDateIncrement(otherData, groupingKey, deliveryMetricOptions, fiscalCal),
    [deliveryMetricOptions, groupingKey, otherData, fiscalCal]
  );

  const joinedDeliveryData = useMemo(() => {
    if (dataGroupedByDate && otherDateDataGroupedByDate) {
      const emptyCell: DeliveryMetricValues = {};
      for (const option of deliveryMetricOptions) {
        emptyCell[option.value] = 0;
      }
      for (const feeMetric of FEE_BREAKOUT_METRICS) {
        emptyCell[feeMetric] = 0;
      }
      return joinDeliveryData(
        dataGroupedByDate,
        otherDateDataGroupedByDate,
        sortedDimensionKeys,
        includeOtherDateRange,
        includeOtherDateRange && selectedLegendItemIndexes != null,
        emptyCell
      );
    }
    return [];
  }, [
    dataGroupedByDate,
    deliveryMetricOptions,
    includeOtherDateRange,
    otherDateDataGroupedByDate,
    selectedLegendItemIndexes,
    sortedDimensionKeys
  ]);
  // When we have an active legend item, we only want to show the data for that item. The graph
  // data should never be affected by selected dates however.
  const filteredGraphDeliveryData = useMemo(() => {
    if (selectedLegendItemIndexes == null) {
      return joinedDeliveryData;
    }
    const filtered: JoinedDeliveryRow[] = [];
    for (const row of joinedDeliveryData) {
      const newRow: JoinedDeliveryRow = { date: row.date, data: {}, otherData: {} };
      for (const index of selectedLegendItemIndexes) {
        const activeDataKey = sortedDimensionKeys[index];
        newRow.data[activeDataKey] = row.data[activeDataKey];
        newRow.otherData[activeDataKey] = row.otherData[activeDataKey];
      }
      filtered.push(newRow);
    }
    return filtered;
  }, [joinedDeliveryData, selectedLegendItemIndexes, sortedDimensionKeys]);

  const [overviewData, overviewByDimensionData] = useMemo(() => {
    const newOverviewData: Record<string, OverviewTotal> = {
      [efficiencyMetric]: { primary: 0, comparison: 0 }
    };
    for (const option of deliveryMetricOptions) {
      newOverviewData[option.value] = { primary: 0, comparison: 0 };
    }
    const newOverviewByDimensionData: Record<string, Record<string, OverviewTotal>> = {};
    if (joinedDeliveryData) {
      // If we hover over or select a specific date in the cart, we need to update the legend
      // data. The graph data should not be affected by this, only greyed out.
      const filterIndex = selectedDateIndex ?? hoveredDateIndex;
      const slicedJoinedDeliveryData =
        filterIndex == null
          ? joinedDeliveryData
          : joinedDeliveryData.slice(filterIndex, filterIndex + 1);
      for (const row of slicedJoinedDeliveryData) {
        if (row.data) {
          for (const key of Object.keys(row.data)) {
            newOverviewByDimensionData[key] = newOverviewByDimensionData[key] || {};
            for (const option of deliveryMetricOptions) {
              newOverviewData[option.value].primary += row.data[key][option.value];
              if (newOverviewByDimensionData[key][option.value]) {
                newOverviewByDimensionData[key][option.value].primary +=
                  row.data[key][option.value];
              } else {
                newOverviewByDimensionData[key] = {
                  ...newOverviewByDimensionData[key],
                  [option.value]: { primary: row.data[key][option.value], comparison: 0 }
                };
              }
            }
          }
        }
        if (row.otherData) {
          for (const key of Object.keys(row.otherData)) {
            newOverviewByDimensionData[key] = newOverviewByDimensionData[key] || {};
            for (const option of deliveryMetricOptions) {
              newOverviewData[option.value].comparison += row.otherData[key][option.value];
              if (newOverviewByDimensionData[key][option.value]) {
                newOverviewByDimensionData[key][option.value].comparison +=
                  row.otherData[key][option.value];
              } else {
                newOverviewByDimensionData[key] = {
                  ...newOverviewByDimensionData[key],
                  [option.value]: { primary: 0, comparison: row.otherData[key][option.value] }
                };
              }
            }
          }
        }
      }
      const efficiencyMetaData = columnMetaData[efficiencyMetric];
      if (efficiencyMetaData?.requiredColumns && efficiencyMetaData.aggregator) {
        const { aggregator, requiredColumns } = efficiencyMetaData;
        const primaryDataObj: Record<string, number> = {};
        const comparisonDataObj: Record<string, number> = {};
        for (const col of requiredColumns) {
          primaryDataObj[col] = newOverviewData[col] ? newOverviewData[col].primary : 0;
          comparisonDataObj[col] = newOverviewData[col] ? newOverviewData[col].comparison : 0;
        }
        const primaryVal = aggregator(primaryDataObj);
        const compVal = aggregator(comparisonDataObj);
        newOverviewData[efficiencyMetric].primary = typeof primaryVal === 'number' ? primaryVal : 0;
        newOverviewData[efficiencyMetric].comparison = typeof compVal === 'number' ? compVal : 0;
      }
    }
    return [newOverviewData, newOverviewByDimensionData];
  }, [
    deliveryMetricOptions,
    hoveredDateIndex,
    joinedDeliveryData,
    selectedDateIndex,
    columnMetaData,
    efficiencyMetric
  ]);

  /// ////////////
  // Graph
  /// ////////////
  /* Chart */
  const grid = (
    <CartesianGrid
      stroke={CARTESIAN_GRID_STROKE}
      strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
      vertical={false}
    />
  );
  const getEfficiencyValue = useCallback(
    (val: JoinedDeliveryRow) => {
      const efficiencyMetaData = columnMetaData[efficiencyMetric];
      if (!efficiencyMetaData?.requiredColumns || !efficiencyMetaData.aggregator) {
        return 0;
      }
      const { aggregator, requiredColumns } = efficiencyMetaData;
      const primaryDataObj: Record<string, number> = {};
      const comparisonDataObj: Record<string, number> = {};
      for (const col of requiredColumns) {
        primaryDataObj[col] = 0;
        comparisonDataObj[col] = 0;
      }

      if (val?.data) {
        for (const dimensionKey of Object.keys(val.data)) {
          for (const col of Object.keys(primaryDataObj)) {
            primaryDataObj[col] += val.data[dimensionKey][col];
          }
        }
      }

      const primaryVal = aggregator(primaryDataObj);
      const efficiencyValue = typeof primaryVal === 'number' ? primaryVal : 0;

      if (efficiencyValue === 0 && val?.otherData && includeOtherDateRange) {
        for (const dimensionKey of Object.keys(val.otherData)) {
          for (const col of Object.keys(comparisonDataObj)) {
            // NOTE: preserved from the original bpm-frontend implementation, which reads
            // `val.data` here rather than `val.otherData` — this looks like a pre-existing
            // bug (the comparison total ends up summing the primary data again), but fixing
            // it is a behavior change outside the scope of this migration pass.
            comparisonDataObj[col] += val.data[dimensionKey][col];
          }
        }
      }
      const compVal = aggregator(comparisonDataObj);
      const otherEfficiencyValue = typeof compVal === 'number' ? primaryVal : 0;
      return efficiencyValue || otherEfficiencyValue;
    },
    [includeOtherDateRange, columnMetaData, efficiencyMetric]
  );
  // Recharts 3 draws a labeled tick at the very top of a data-max domain (an unrounded value
  // that overflows the axis), so tick only at nice steps below the max.
  const [leftYAxisTicks, rightYAxisTicks] = useMemo(() => {
    let leftMax = 0;
    let rightMax = 0;
    for (const row of filteredGraphDeliveryData ?? []) {
      let primaryStack = 0;
      let comparisonStack = 0;
      for (const dimensionKey of Object.keys(row.data)) {
        const primaryVal = row.data[dimensionKey]?.[metric.value] || 0;
        const comparisonVal =
          (includeOtherDateRange && row.otherData[dimensionKey]?.[metric.value]) || 0;
        primaryStack += primaryVal || comparisonVal;
        comparisonStack += comparisonVal;
      }
      leftMax = Math.max(leftMax, primaryStack, comparisonStack);
      rightMax = Math.max(rightMax, getEfficiencyValue(row) ?? 0);
    }
    return [absolute ? getNiceTicks(leftMax) : undefined, getNiceTicks(rightMax)];
  }, [absolute, filteredGraphDeliveryData, getEfficiencyValue, includeOtherDateRange, metric]);
  const yAxes = useMemo(
    () => [
      <YAxis
        key='leftYAxis'
        width={currSymbol === 'CA$' ? 68 : 60}
        axisLine={false}
        domain={Y_AXIS_DOMAIN}
        padding={Y_AXIS_PADDING}
        scale='linear'
        ticks={leftYAxisTicks}
        tick={TICK_STYLE}
        tickFormatter={number =>
          `${metric.isSpend && absolute ? currSymbol : ''}${
            absolute ? abbreviateNumber(number) : number * 100.0
          }${absolute ? '' : '%'}`
        }
        tickLine={false}
        yAxisId={LEFT_Y_AXIS_ID}
      />,
      <YAxis
        key='rightYAxis'
        axisLine={false}
        domain={Y_AXIS_DOMAIN}
        orientation='right'
        padding={Y_AXIS_PADDING}
        scale='linear'
        ticks={rightYAxisTicks}
        tick={TICK_STYLE}
        tickFormatter={
          CURRENCY_EFFICIENCY_METRICS.includes(efficiencyMetric)
            ? number => `${currSymbol}${abbreviateNumber(number)}`
            : number => toPrettyPercent(number, { decimals: 1 })
        }
        tickLine={false}
        yAxisId={RIGHT_Y_AXIS_ID}
      />
    ],
    [absolute, metric, currSymbol, efficiencyMetric, leftYAxisTicks, rightYAxisTicks]
  );

  const graphYAxisTitles = !showPercentChange && (
    <div className='dc-above-graph-container'>
      <div className='dc-above-graph' style={{ width: graphWidth }}>
        <div className='dc-left-title'>{metric.label}</div>
        <div className='dc-right-title'>
          {columnMetaData[efficiencyMetric]?.prettyName || efficiencyMetric}
        </div>
      </div>
      <div className='divider-line' />
    </div>
  );
  const xAxis = (
    <XAxis
      axisLine={false}
      dataKey='date'
      padding={X_AXIS_PADDING}
      tick={TICK_STYLE}
      tickFormatter={date => formatDateLabel(date, fiscalCal, groupingKey)}
    />
  );

  const bars = useMemo(() => {
    if (!joinedDeliveryData) return undefined;
    const barElements: JSX.Element[] = [];
    if (includeOtherDateRange && selectedLegendItemIndexes != null) {
      const primaryDates = Object.keys(dataGroupedByDate || {}).sort((a, b) => a.localeCompare(b));
      const comparisonDates = Object.keys(otherDateDataGroupedByDate || {}).sort((a, b) =>
        a.localeCompare(b)
      );
      const longerRange =
        primaryDates.length > comparisonDates.length ? primaryDates : comparisonDates;
      for (let dateIndex = 0; dateIndex < longerRange.length; dateIndex++) {
        barElements.push(
          <Bar
            fill={getChannelSeriesColor(selectedLegendItemIndexes[0], usePrimaryPalette)}
            isAnimationActive={false}
            key={`${dateIndex} ${PRIMARY_STACK_ID}`}
            stackId={PRIMARY_STACK_ID}
            yAxisId={LEFT_Y_AXIS_ID}
            dataKey={(val: JoinedDeliveryRow) =>
              val?.data?.[sortedDimensionKeys[selectedLegendItemIndexes[0]]]?.[metric.value] || 0
            }
          />
        );
        barElements.push(
          <Bar
            fill={GREYED_OUT_GREY}
            isAnimationActive={false}
            key={`${dateIndex} ${COMPARISON_STACK_ID}`}
            stackId={COMPARISON_STACK_ID}
            yAxisId={LEFT_Y_AXIS_ID}
            dataKey={(val: JoinedDeliveryRow) =>
              val?.otherData?.[sortedDimensionKeys[selectedLegendItemIndexes[0]]]?.[metric.value] ||
              0
            }
          />
        );
      }
      return barElements;
    }
    for (let dimensionKeyIndex = 0; dimensionKeyIndex < activeKeyCount; ++dimensionKeyIndex) {
      const cells: JSX.Element[] = [];
      const fill =
        (selectedLegendItemIndexes != null &&
          !selectedLegendItemIndexes.includes(dimensionKeyIndex)) ||
        (selectedLegendItemIndexes == null &&
          hoveredLegendItemIndex != null &&
          hoveredLegendItemIndex !== dimensionKeyIndex)
          ? GREYED_OUT_GREY
          : getChannelSeriesColor(dimensionKeyIndex, usePrimaryPalette);
      for (let dateIndex = 0; dateIndex < joinedDeliveryData.length; ++dateIndex) {
        cells.push(
          <Cell
            fill={fill}
            key={dateIndex}
            opacity={
              (selectedDateIndex != null && selectedDateIndex !== dateIndex) ||
              (selectedDateIndex == null &&
                hoveredDateIndex != null &&
                hoveredDateIndex !== dateIndex)
                ? 0.3
                : 1
            }
            onClick={() => {
              const newDateIndex =
                selectedDateIndex == null || dateIndex !== selectedDateIndex
                  ? dateIndex
                  : undefined;
              setSelectedDateIndex(newDateIndex);
            }}
            onMouseLeave={() => setHoveredDateIndex(undefined)}
            onMouseOver={() => {
              if (!hoveredDateIndex && selectedDateIndex == null) {
                setHoveredDateIndex(dateIndex);
              }
            }}
          />
        );
      }
      barElements.push(
        <Bar
          isAnimationActive={false}
          key={dimensionKeyIndex}
          stackId={PRIMARY_STACK_ID}
          yAxisId={LEFT_Y_AXIS_ID}
          dataKey={(val: JoinedDeliveryRow) => {
            const primaryVal =
              val?.data?.[sortedDimensionKeys[dimensionKeyIndex]]?.[metric.value] || 0;
            const comparisonVal =
              (includeOtherDateRange &&
                val?.otherData?.[sortedDimensionKeys[dimensionKeyIndex]]?.[metric.value]) ||
              0;
            return primaryVal || comparisonVal;
          }}
        >
          {cells}
        </Bar>
      );
    }
    return barElements;
  }, [
    activeKeyCount,
    dataGroupedByDate,
    hoveredDateIndex,
    hoveredLegendItemIndex,
    includeOtherDateRange,
    joinedDeliveryData,
    metric,
    otherDateDataGroupedByDate,
    selectedDateIndex,
    selectedLegendItemIndexes,
    sortedDimensionKeys,
    usePrimaryPalette
  ]);
  const EfficiencyMetricLine = useMemo(
    () => (
      <Line
        dot={false}
        isAnimationActive={false}
        name='CPM'
        stroke='black'
        strokeWidth={3}
        scale='linear'
        type='monotone'
        yAxisId={RIGHT_Y_AXIS_ID}
        dataKey={getEfficiencyValue}
      />
    ),
    [getEfficiencyValue]
  );

  /* Legend */
  const legendDateRange = useMemo(() => {
    if (joinedDeliveryData && (selectedDateIndex != null || hoveredDateIndex != null)) {
      const dateIndex = (selectedDateIndex ?? hoveredDateIndex) as number;
      const dateRange = (
        <div className='legend-date-range'>
          <MdArrowBackIos
            onClick={() => {
              if (dateIndex > 0) {
                setSelectedDateIndex(dateIndex - 1);
              }
            }}
            style={{
              opacity: dateIndex > 0 ? 1 : 0
            }}
          />
          <div className='primary'>
            {formatDateLabel(joinedDeliveryData[dateIndex].date, fiscalCal, groupingKey)}
          </div>
          <MdArrowForwardIos
            onClick={() => {
              if (dateIndex < joinedDeliveryData.length - 1) {
                setSelectedDateIndex(dateIndex + 1);
              }
            }}
            style={{
              opacity: dateIndex < joinedDeliveryData.length - 1 ? 1 : 0
            }}
          />
        </div>
      );
      return <div className='legend-date-range-container'>{dateRange}</div>;
    }
    const dateRange = (
      <div className='legend-date-range'>
        <div className='primary'>{`${formatDateLabel(dates.start, fiscalCal)} – ${formatDateLabel(
          dates.end,
          fiscalCal
        )}`}</div>
        {includeOtherDateRange && <div className='vs'>vs</div>}
        {includeOtherDateRange && otherDates && (
          <div className='comparison'>{`${formatDateLabel(
            otherDates.start,
            fiscalCal
          )} – ${formatDateLabel(otherDates.end, fiscalCal)}`}</div>
        )}
      </div>
    );
    return <div className='legend-date-range-container'>{dateRange}</div>;
  }, [
    dates,
    groupingKey,
    hoveredDateIndex,
    includeOtherDateRange,
    joinedDeliveryData,
    otherDates,
    selectedDateIndex,
    fiscalCal
  ]);

  const legendEfficiencyMetricContainer = useMemo(() => {
    let metricTotal = overviewData[metric.value].primary;
    let totalEfficiencyMetric = overviewData[efficiencyMetric].primary;
    const metricPercentageChange =
      overviewData[metric.value].primary && overviewData[metric.value].comparison
        ? ((overviewData[metric.value].primary - overviewData[metric.value].comparison) /
            overviewData[metric.value].comparison) *
          100
        : 0;
    const efficiencyPercentageChange =
      overviewData[efficiencyMetric].primary && overviewData[efficiencyMetric].comparison
        ? ((overviewData[efficiencyMetric].primary - overviewData[efficiencyMetric].comparison) /
            overviewData[efficiencyMetric].comparison) *
          100
        : 0;
    let isTotal = hoveredDateIndex == null && selectedDateIndex == null;
    if (selectedLegendItemIndexes != null || hoveredLegendItemIndex != null) {
      isTotal = false;
      const indexes: number[] = selectedLegendItemIndexes ?? [hoveredLegendItemIndex as number];
      metricTotal = indexes.reduce(
        (sum, index) =>
          sum + overviewByDimensionData[sortedDimensionKeys[index]][metric.value].primary,
        0
      );
      const efficiencyMetaData = columnMetaData[efficiencyMetric];
      if (efficiencyMetaData?.requiredColumns && efficiencyMetaData.aggregator) {
        const { aggregator, requiredColumns } = efficiencyMetaData;
        const aggDataObj: Record<string, number> = {};
        for (const col of requiredColumns) {
          aggDataObj[col] = indexes.reduce(
            (sum, index) => sum + overviewByDimensionData[sortedDimensionKeys[index]][col].primary,
            0
          );
        }
        const primaryVal = aggregator(aggDataObj);
        totalEfficiencyMetric = typeof primaryVal === 'number' ? primaryVal : 0;
      }
    }
    let formattedMetricTotal;
    if (metric.isSpend) {
      formattedMetricTotal = spendFormatter(metricTotal);
    } else if (metric.label === 'Impressions') {
      formattedMetricTotal = numberFormatter(metricTotal, { zeroLabel: '<1000', min: 1000 });
    } else {
      formattedMetricTotal = numberFormatter(metricTotal, { zeroLabel: '<1', min: 1 });
    }
    return (
      <div className='legend-cpm-container'>
        <div className='spend-imp'>
          <div className='icon-container'>
            <div className='icon' />
          </div>
          <div className='label'>
            {isTotal ? 'Total ' : ''}
            {metric.label}
            {!isTotal && <b style={{ opacity: 0 }}>{'Total '}</b>}
          </div>
          <div className='value-container'>
            <div className='value'>{formattedMetricTotal}</div>
            {includeOtherDateRange && (
              <div>
                <PercentChange
                  percentChange={metricPercentageChange}
                  size={Size.SM}
                  stretchMode={StretchMode.FIT}
                />
              </div>
            )}
          </div>
        </div>
        <div className='cpm'>
          <div className='icon-container'>
            <div className='icon' />
          </div>
          <div className='label'>
            {`${isTotal ? 'Overall ' : ''}${METRIC_TO_PRETTY_NAME[efficiencyMetric]}`}
            {!isTotal && <b style={{ opacity: 0 }}>{'Overall '}</b>}
          </div>
          <div className='value-container'>
            <div className='value'>{metricFormatter(efficiencyMetric)(totalEfficiencyMetric)}</div>
            {includeOtherDateRange && (
              <div className='percent-change-container'>
                <PercentChange
                  percentChange={efficiencyPercentageChange}
                  size={Size.SM}
                  stretchMode={StretchMode.FIT}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }, [
    hoveredDateIndex,
    hoveredLegendItemIndex,
    includeOtherDateRange,
    metric,
    overviewByDimensionData,
    overviewData,
    selectedDateIndex,
    selectedLegendItemIndexes,
    sortedDimensionKeys,
    spendFormatter,
    numberFormatter,
    metricFormatter,
    columnMetaData,
    efficiencyMetric
  ]);
  const legendItems = useMemo(() => {
    const legendItemElements: JSX.Element[] = [];
    for (let itemIndex = 0; itemIndex < sortedDimensionKeys.length; itemIndex++) {
      const key = sortedDimensionKeys[itemIndex];
      if (!overviewByDimensionData[key]) continue;

      const percentChange =
        overviewByDimensionData[key][metric.value].primary &&
        overviewByDimensionData[key][metric.value].comparison
          ? ((overviewByDimensionData[key][metric.value].primary -
              overviewByDimensionData[key][metric.value].comparison) /
              overviewByDimensionData[key][metric.value].comparison) *
            100
          : 0;
      const compareItem =
        includeOtherDateRange &&
        otherDates &&
        selectedLegendItemIndexes &&
        selectedLegendItemIndexes.includes(itemIndex);
      const colorContainer = (
        <div className='color-container'>
          <div
            className='color'
            style={{
              backgroundColor: getChannelSeriesColor(itemIndex, usePrimaryPalette)
            }}
          />
        </div>
      );
      // Icon specs are keyed by the snake_case name. The lambda sends each value's icon key
      // columns (e.g. `network_short_code`) in `keys`.
      const dimensionVarName = deliveryDimensionMap[dimension];
      const { url } = getDimensionCell(
        { [dimensionVarName]: key, ...data?.keys?.[key] } as unknown as DimensionMap,
        {
          dimensionTypeName: dimension,
          dimensionVarName,
          icon: true
        } as unknown as DimensionColumn
      );
      const iconContainer = key && data?.keys && url && (
        <div className='icon-container'>
          <Img className='icon' src={url} />
        </div>
      );
      const labelContainer = (
        <div className='label-container'>
          {colorContainer}
          {iconContainer}
          <div className='label' title={key}>
            {key}
          </div>
        </div>
      );
      const percentageChangeContainer = (
        <div className='percent-change-container'>
          <PercentChange
            percentChange={percentChange}
            size={Size.SM}
            stretchMode={StretchMode.FIT}
          />
        </div>
      );
      const percentOfTotal =
        overviewData[metric.value].primary && overviewData[metric.value].primary > 0
          ? (overviewByDimensionData[key][metric.value].primary /
              overviewData[metric.value].primary) *
            100
          : 0;
      let formattedValue;
      if (!absolute) {
        const percentOfTotalDecimals = percentOfTotal > 1 || percentOfTotal === 0 ? 0 : 1;
        formattedValue = `${percentOfTotal.toFixed(percentOfTotalDecimals)}%`;
      } else if (metric.isSpend) {
        formattedValue = spendFormatter(overviewByDimensionData[key][metric.value].primary);
      } else {
        formattedValue = numberFormatter(overviewByDimensionData[key][metric.value].primary);
      }
      const valueContainer = (
        <div className='value-container'>
          <div className='value'>{formattedValue}</div>
          {includeOtherDateRange && percentageChangeContainer}
        </div>
      );
      const comparisonContainer = includeOtherDateRange &&
        otherDates &&
        selectedLegendItemIndexes &&
        selectedLegendItemIndexes.includes(itemIndex) && (
          <div
            className='comparison-container'
            style={{
              border: `1px solid ${getChannelSeriesColor(itemIndex, usePrimaryPalette)}`
            }}
          >
            <div className='item-description'>
              <div className='label-container'>
                {iconContainer}
                <div className='label' title={key}>
                  {key}
                </div>
              </div>
              <div className='value-container'>{percentageChangeContainer}</div>
            </div>

            <div className='date-container'>
              {colorContainer}
              <div className='date-range'>{`${formatDateLabel(
                dates.start,
                fiscalCal
              )} – ${formatDateLabel(dates.end, fiscalCal)}`}</div>
              <div className='value'>
                <div>
                  {metric.isSpend
                    ? spendFormatter(overviewByDimensionData[key][metric.value].primary)
                    : numberFormatter(overviewByDimensionData[key][metric.value].primary)}
                </div>
              </div>
            </div>
            <div className='date-container'>
              <div className='color-container'>
                <div
                  className='color'
                  style={{
                    backgroundColor: GREYED_OUT_GREY
                  }}
                />
              </div>
              <div className='date-range'>{`${formatDateLabel(
                otherDates.start,
                fiscalCal
              )} – ${formatDateLabel(otherDates.end, fiscalCal)}`}</div>
              <div className='value'>
                {metric.isSpend
                  ? spendFormatter(overviewByDimensionData[key][metric.value].comparison)
                  : numberFormatter(overviewByDimensionData[key][metric.value].comparison)}
              </div>
            </div>
          </div>
        );
      legendItemElements.push(
        <InteractiveDiv
          key={`legend-item-container${itemIndex}`}
          className={cn('legend-item-container', {
            'greyed-out':
              selectedLegendItemIndexes != null && !selectedLegendItemIndexes.includes(itemIndex)
          })}
          onClick={() => {
            if (
              selectedLegendItemIndexes == null ||
              (includeOtherDateRange && selectedLegendItemIndexes[0] !== itemIndex)
            ) {
              setSelectedLegendItemIndexes([itemIndex]);
            } else {
              const isNew = !selectedLegendItemIndexes.includes(itemIndex);
              if (isNew && !includeOtherDateRange) {
                setSelectedLegendItemIndexes(
                  [...selectedLegendItemIndexes, itemIndex].sort((a, b) => a - b)
                );
              } else {
                const newIndexes = selectedLegendItemIndexes.filter(index => index !== itemIndex);
                setSelectedLegendItemIndexes(newIndexes.length > 0 ? newIndexes : undefined);
              }
            }
          }}
          onMouseLeave={() => setHoveredLegendItemIndex(undefined)}
          onMouseOver={() => {
            if (!hoveredLegendItemIndex && selectedLegendItemIndexes == null) {
              setHoveredLegendItemIndex(itemIndex);
            }
          }}
          onFocus={() => {
            if (!hoveredLegendItemIndex && selectedLegendItemIndexes == null) {
              setHoveredLegendItemIndex(itemIndex);
            }
          }}
        >
          {!compareItem && labelContainer}
          {!compareItem && valueContainer}
          {comparisonContainer}
        </InteractiveDiv>
      );
    }
    return <div className='legend-items'>{legendItemElements}</div>;
  }, [
    absolute,
    data,
    dates,
    dimension,
    deliveryDimensionMap,
    getDimensionCell,
    hoveredLegendItemIndex,
    includeOtherDateRange,
    metric,
    otherDates,
    overviewByDimensionData,
    overviewData,
    selectedLegendItemIndexes,
    sortedDimensionKeys,
    usePrimaryPalette,
    spendFormatter,
    numberFormatter,
    fiscalCal
  ]);
  const legendSelectAll = selectedLegendItemIndexes != null && (
    <InteractiveDiv
      className='select-all-label'
      onClick={() => setSelectedLegendItemIndexes(undefined)}
    >
      Select All
    </InteractiveDiv>
  );
  const legend = (
    <div className='dc-legend'>
      {legendDateRange}
      {legendEfficiencyMetricContainer}
      <div className='legend-divider' />
      {legendSelectAll}
      {legendItems}
    </div>
  );

  /* Graph */
  const graph = (
    <div className='dc-graph'>
      {graphYAxisTitles}
      <div className='dc-graph-contents'>
        <div id='dc-graph' className='dc-graph' ref={graphContainerRef}>
          <AutoSizer
            renderProp={({ width = 0, height = 0 }) => (
              <ComposedChart
                data={filteredGraphDeliveryData}
                height={height}
                margin={{ bottom: -10 }}
                stackOffset={absolute ? 'none' : 'expand'}
                width={width}
              >
                {grid}
                {bars}
                {EfficiencyMetricLine}
                {xAxis}
                {yAxes}
              </ComposedChart>
            )}
          />
        </div>
        <div className='right-of-chart'>{legend}</div>
      </div>
    </div>
  );

  const tableData = useMemo(() => {
    const rows: DeliveryTableRow[] = [];
    if (dataGroupedByDate && otherDateDataGroupedByDate && filteredGraphDeliveryData) {
      const sortedDataDateKeys = Object.keys(dataGroupedByDate).sort((a, b) => a.localeCompare(b));
      const sortedOtherDataDateKeys = Object.keys(otherDateDataGroupedByDate).sort((a, b) =>
        a.localeCompare(b)
      );
      const length = Math.max(sortedDataDateKeys.length, sortedOtherDataDateKeys.length);
      const dataKeysMap: Record<string, string> = {};
      for (const row of filteredGraphDeliveryData) {
        for (const key of Object.keys(row.data)) {
          dataKeysMap[key] = key;
        }
        for (const key of Object.keys(row.otherData)) {
          dataKeysMap[key] = key;
        }
      }
      const combinedKeys = Object.keys(dataKeysMap);
      for (let i = 0; i < length; i++) {
        for (const dimensionValue of combinedKeys) {
          const date = sortedDataDateKeys[i];
          const rowData = dataGroupedByDate[sortedDataDateKeys[i]]?.[dimensionValue] || {};
          const rowOtherData =
            otherDateDataGroupedByDate[sortedOtherDataDateKeys[i]]?.[dimensionValue] || {};
          const otherDate = sortedOtherDataDateKeys[i];
          rows.push({
            data: rowData,
            date,
            dimensionValue,
            otherData: rowOtherData,
            otherDate
          });
        }
      }
    }
    return rows;
  }, [dataGroupedByDate, filteredGraphDeliveryData, otherDateDataGroupedByDate]);

  // Table Logic
  const [headers, superHeaders] = useMemo(() => {
    let computedSuperHeaders: SuperTopItemType<string>[] | undefined;
    const computedHeaders: TypedHeader<DeliveryTableRow>[] = [
      {
        flex: 1,
        label: dimension,
        name: 'dimensionValue',
        nonInteractive: true,
        renderer: item => item.dimensionValue || '-'
      },
      {
        label: 'Date',
        name: 'date',
        nonInteractive: true,
        width: 80,
        renderer: item => formatDateLabel(item.date, fiscalCal, '', true) || '--'
      }
    ];
    for (const option of deliveryMetricOptions) {
      computedHeaders.push({
        flex: 1,
        label: option.label,
        name: option.value,
        nonInteractive: true,
        renderer: item =>
          option.isSpend
            ? spendFormatter(item.data[option.value] || 0)
            : numberFormatter(item.data[option.value] || 0)
      });
    }
    if (includeOtherDateRange) {
      computedHeaders.push({
        label: 'Comparison Date',
        name: 'otherDate',
        nonInteractive: true,
        width: 80,
        renderer: item => formatDateLabel(item.otherDate, fiscalCal, '', true) || '--'
      });
      for (const option of deliveryMetricOptions) {
        computedHeaders.push({
          flex: 1,
          label: option.label,
          name: option.value,
          nonInteractive: true,
          renderer: item =>
            option.isSpend
              ? spendFormatter(item.otherData[option.value] || 0)
              : numberFormatter(item.otherData[option.value] || 0)
        });
      }
      const superHeaderSpan = deliveryMetricOptions.length + 1;
      computedSuperHeaders = [
        { span: 1, data: '' },
        { span: superHeaderSpan, data: 'Primary Period' },
        { span: superHeaderSpan, data: 'Comparison Period' }
      ];
    }
    return [computedHeaders, computedSuperHeaders];
  }, [
    deliveryMetricOptions,
    dimension,
    includeOtherDateRange,
    spendFormatter,
    numberFormatter,
    fiscalCal
  ]);

  const excelDownload = useCallback(() => {
    const unionData = joinedDeliveryData.map(row => {
      const impressions = sortedDimensionKeys.reduce(
        (sum, key) => sum + (row.data[key]?.count ?? row.data[key]?.impressions ?? 0),
        0
      );
      const otherImpressions =
        impressions || !includeOtherDateRange
          ? 0
          : sortedDimensionKeys.reduce(
              (sum, key) =>
                sum + (row.otherData[key]?.count ?? row.otherData[key]?.impressions ?? 0),
              0
            );
      const spend = sortedDimensionKeys.reduce(
        (sum, key) => sum + (row.data[key]?.cost ?? row.data[key]?.spend ?? 0),
        0
      );
      const otherSpend =
        spend || !includeOtherDateRange
          ? 0
          : sortedDimensionKeys.reduce(
              (sum, key) => sum + (row.otherData[key]?.cost ?? row.otherData[key]?.spend ?? 0),
              0
            );
      const sumFee = (rows: typeof row.data, feeMetric: string) =>
        sortedDimensionKeys.reduce((sum, key) => sum + (rows[key]?.[feeMetric] ?? 0), 0);
      const mediaCost = sumFee(row.data, 'mediaCost');
      const nonMediaCost = sumFee(row.data, 'nonMediaCost');
      return {
        date: row.date,
        impressions: impressions || otherImpressions,
        spend: spend || otherSpend,
        mediaCost: mediaCost || (includeOtherDateRange ? sumFee(row.otherData, 'mediaCost') : 0),
        nonMediaCost:
          nonMediaCost || (includeOtherDateRange ? sumFee(row.otherData, 'nonMediaCost') : 0)
      };
    });
    exportToExcel(unionData, `${company}_Compare_Metrics`);
  }, [company, includeOtherDateRange, joinedDeliveryData, sortedDimensionKeys]);

  const pngDownload = useCallback(async () => {
    await downloadPNG('.dc-graph', `${company}_Compare_Metrics`);
  }, [company]);

  // Only relevant for Linear
  const postLogsDownload = useCallback(async () => {
    setDownloadingFromServer(true);
    try {
      const postLogsData = await fetchWithCometFallback<
        { kpi: string; start: string; end: string },
        { bucket: string; key: string }
      >({
        service: LinearLambdaFetch,
        standardEndpoint: '/processed_post_logs',
        input: {
          kpi: company,
          start: dates.start,
          end: dates.end
        }
      });
      await pollS3({
        bucket: postLogsData.bucket,
        mimeType: 'text/csv',
        filename: postLogsData.key,
        overloadFilename: `Post Logs ${company} from ${dates.start} to ${dates.end}.csv`
      });
    } catch (e) {
      setError({ message: `Failed to fetch post logs data for ${company}.`, reportError: e });
    }
    setDownloadingFromServer(false);
  }, [dates, company, setError]);

  // The full-granularity export (device, OS, network, creative, ...), generated server-side the
  // same way App 1.0 does. Unlike the chart view, it ignores the chart's dimension and compare range.
  const fullDetailDownload = useCallback(async () => {
    setDownloadingFromServer(true);
    try {
      const urlRes = await StreamingV2LambdaFetch('/delivery_csv_v2', {
        params: buildDeliveryCsvParams({ company, dates, prefix, filterID, filterState })
      });
      const { url } = await awaitJSON<{ url: string }>(urlRes);
      const csvRes = await fetchPresigned(url, 'Full detail download failed');
      // The server hands back a CSV; convert it so both Delivery downloads are XLSX. Only the
      // numeric columns are typed, so ISCI and Creative values keep their leading zeros.
      const { data: csvRows } = Papa.parse<Record<string, unknown>>(await csvRes.text(), {
        header: true,
        skipEmptyLines: true
      });
      const rows = csvRows.map(row => {
        const typed = { ...row };
        DELIVERY_NUMERIC_COLUMNS.forEach(col => {
          if (typed[col] !== undefined && typed[col] !== '') {
            typed[col] = Number(typed[col]);
          }
        });
        return typed;
      });
      await exportToExcel(rows, `${company} Delivery ${dates.start} to ${dates.end}`);
    } catch (e) {
      setError({
        message: `Couldn't fetch the full-detail Delivery export for ${company}.`,
        reportError: e
      });
    } finally {
      setDownloadingFromServer(false);
    }
  }, [company, dates, filterID, filterState, prefix, setError]);

  const table = (
    <BPMTable
      data={tableData}
      filterBar={false}
      headerHeight={30}
      headers={headers}
      superHeaderHeight={30}
      superHeaders={superHeaders}
    />
  );

  // Title Pickers
  const dateGroupingPicker = (
    <Dropdown
      type={DropdownToggleType.WIDGET_TITLE}
      stretch={StretchMode.FIT}
      value={groupingKey}
      options={dateGroupingOptions}
      onChange={option => {
        setGroupingKey(option);
        setSelectedDateIndex(undefined);
        setHoveredDateIndex(undefined);
      }}
    />
  );
  const metricPicker = (
    <Dropdown
      type={DropdownToggleType.WIDGET_TITLE}
      stretch={StretchMode.FIT}
      value={metric.value}
      options={deliveryMetricOptions}
      onChange={option => {
        const newOption = deliveryMetricOptions.find(opt => opt.value === option);
        setMetric(newOption ?? deliveryMetricOptions[0]);
      }}
    />
  );

  const dimensionDropdownOptions = useMemo(
    () =>
      Object.keys(deliveryDimensionMap).map(opt => {
        const conflictingDims = incompatibleDeliveryDimsFromFilter[opt];
        if (conflictingDims && conflictingDims.length > 0) {
          return {
            value: opt,
            label: opt,
            disabled: true,
            simpleTooltip: makeInvalidDimMsg({
              prettyDims: [opt],
              isInvalidAnalyticsDim: false,
              conflictingFilterDims: conflictingDims
            }),
            simpleTooltipVerticalShift: '-55px'
          };
        }
        return opt;
      }),
    [deliveryDimensionMap, incompatibleDeliveryDimsFromFilter]
  );

  const dimensionPicker = (
    <Dropdown
      className='single-channel-dimension-dropdown'
      onChange={setDimension}
      options={dimensionDropdownOptions}
      stretch={StretchMode.FIT}
      type={DropdownToggleType.WIDGET_TITLE}
      value={dimension}
    />
  );

  // No Data Logic
  const emptyDeliveryData = <div className='empty-delivery-data'>No data to show</div>;

  // Chart Container Logic
  const chartTitle = (
    <>
      {dateGroupingPicker}
      {metricPicker}
      <div className='by-label'>by</div>
      {dimensionPicker}
      <div className='title-divider' />
      <div className='primary-date-range'>{`${formatDateLabel(
        dates.start,
        fiscalCal
      )} – ${formatDateLabel(dates.end, fiscalCal)}`}</div>
      {!includeOtherDateRange &&
        (selectedLegendItemIndexes == null || selectedLegendItemIndexes.length < 2) && (
          <OverlayTrigger
            placement={Placement.RIGHT}
            overlay={<Tooltip id='show-comparison-period'>Show comparison period</Tooltip>}
          >
            <InteractiveDiv
              className='add-other-date-range'
              onClick={() => {
                setIncludeOtherDateRange(true);
                const primaryDates = Object.keys(dataGroupedByDate || {}).sort((a, b) =>
                  a.localeCompare(b)
                );
                const comparisonDates = Object.keys(otherDateDataGroupedByDate || {}).sort((a, b) =>
                  a.localeCompare(b)
                );
                const allDates = [...new Set([...primaryDates, ...comparisonDates])].sort((a, b) =>
                  a.localeCompare(b)
                );
                if (selectedDateIndex) {
                  const selectedDate = joinedDeliveryData[selectedDateIndex].date;
                  const newIndex = allDates.indexOf(selectedDate);
                  if (newIndex !== -1) {
                    setSelectedDateIndex(newIndex);
                  }
                }
              }}
            >
              +
            </InteractiveDiv>
          </OverlayTrigger>
        )}
      {includeOtherDateRange && otherDates && (
        <>
          <div className='other-date-range'>{`vs ${formatDateLabel(
            otherDates.start,
            fiscalCal
          )} – ${formatDateLabel(otherDates.end, fiscalCal)}`}</div>
          {showAddOtherDateRange && (
            <InteractiveDiv
              className='remove-button'
              onClick={() => {
                if (selectedDateIndex != null) {
                  const selectedDate = joinedDeliveryData[selectedDateIndex].date;
                  const start = new Date(dates.start).getTime();
                  const end = new Date(dates.end).getTime();
                  const selected = new Date(selectedDate).getTime();
                  if (selected < start || selected > end) {
                    setSelectedDateIndex(undefined);
                  }
                }
                setIncludeOtherDateRange(false);
                setShowPercentChange(false);
              }}
            >
              <MdClose />
            </InteractiveDiv>
          )}
        </>
      )}
    </>
  );

  // Full detail is the server-side row-level export; the XLSX only reflects what the chart shows.
  const downloadMenu = useMemo(() => {
    const items: { label: string; onClick: () => void }[] = [];
    if (supportsDeliveryCsv(prefix)) {
      items.push({ label: DOWNLOAD_FULL_DETAIL, onClick: fullDetailDownload });
    }
    items.push(
      { label: DOWNLOAD_CHART_VIEW, onClick: excelDownload },
      { label: DOWNLOAD_PNG, onClick: pngDownload }
    );
    if (prefix === 'linear') {
      items.push({ label: DOWNLOAD_POST_LOGS, onClick: postLogsDownload });
    }
    return items;
  }, [fullDetailDownload, excelDownload, pngDownload, postLogsDownload, prefix]);
  const downloadMenuOptions = useMemo(() => downloadMenu.map(i => i.label), [downloadMenu]);
  const downloadMenuOnClickOptions = useMemo(
    () => downloadMenu.map(i => i.onClick),
    [downloadMenu]
  );

  const rightActions = (
    <>
      <Dropdown
        onChange={value => setAbsolute(value === 'Absolute')}
        options={[
          { label: 'Absolute', value: 'Absolute' },
          { label: 'Relative', value: 'Relative' }
        ]}
        size={Size.MD}
        stretch={StretchMode.FIT}
        value={absolute ? 'Absolute' : 'Relative'}
      />
      <GraphTableToggleButton
        graphView={graphView}
        setGraphView={setGraphView}
        size={Size.MD}
        widgetName='single_channel_delivery_chart'
      />
      {downloadingFromServer ? (
        <Spinner />
      ) : (
        <DownloadDropdown
          size={Size.MD}
          menuOptions={downloadMenuOptions}
          onClickOptions={downloadMenuOnClickOptions}
          disabledMenuOptions={{ [DOWNLOAD_PNG]: !graphView }}
        />
      )}
    </>
  );

  useEffect(() => {
    if (!dateGroupingOptions.map(option => option.value).includes(groupingKey)) {
      setGroupingKey(dateGroupingOptions[0].value);
    }
  }, [dateGroupingOptions, groupingKey]);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedDateIndex(undefined);
        setSelectedLegendItemIndexes(undefined);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, []);

  let deliveryBody;
  if (joinedDeliveryData.length === 0) {
    deliveryBody = emptyDeliveryData;
  } else if (graphView) {
    deliveryBody = graph;
  } else {
    deliveryBody = table;
  }

  return (
    <div
      className='single-channel-delivery-chart-container'
      onMouseLeave={() => setShowAddOtherDateRange(false)}
      onMouseOver={() => setShowAddOtherDateRange(true)}
      onFocus={() => setShowAddOtherDateRange(true)}
    >
      <ChartContainer rightActions={rightActions} title={chartTitle}>
        {deliveryBody}
      </ChartContainer>
    </div>
  );
};
